<template>
  <div class="p-6 space-y-8">
    <!-- Create Report Section -->
    <section class="bg-white shadow rounded p-6">
      <h2 class="text-2xl font-bold mb-4">New Player Data Report</h2>

      <!-- hidden dev control -->
      <label class="hidden">
        Max span (days):
        <input
          type="number"
          v-model.number="maxSpanDays"
          min="1"
          class="w-16 text-xs border rounded p-1"
        />
      </label>

      <div v-if="spanExceeded" class="mb-2 text-red-500 text-sm">
        Entry timeframe cannot exceed {{ maxSpanDays }} days.
      </div>

      <!-- Source selector -->
      <div class="mb-4 flex items-center gap-3">
        <label class="inline-flex items-center gap-2">
          <input type="checkbox" v-model="legoPlayersOnly" class="h-4 w-4">
          <span class="text-sm font-medium text-red-700">Integration Players Only</span>
        </label>
        <span class="text-xs text-gray-500">Count unique players that interacted with one of our Integrations.</span>
      </div>

      <div class="mb-4 flex flex-wrap items-start gap-2">
        <!-- Game -->
        <select
          v-if="games.length"
          v-model="newEntry.game_uuid"
          @change="onNewEntryGameChange"
          class="border rounded p-2 flex-1 min-w-[200px]"
        >
          <option disabled value="">Select game…</option>
          <option
            v-for="g in games"
            :key="g.uuid"
            :value="g.uuid"
          >{{ g.friendly_name }}</option>
        </select>
        <LoadingComponent v-if="!games.length && !gameFetchError" class="inline-block mr-2" />
        <div v-if="gameFetchError" class="text-red-500">Failed to load games. Please try again later.</div>

        <!-- Dates -->
        <input
          type="date"
          v-model="newEntry.start_date"
          :max="newEntry.end_date || null"
          @change="onSpanChange"
          class="border rounded p-2"
        />
        <input
          type="date"
          v-model="newEntry.end_date"
          :min="newEntry.start_date || null"
          :max="endMaxDate"
          @change="onSpanChange"
          class="border rounded p-2"
        />

        <!-- Integration-only optional event dropdown -->
        <div class="min-w-[220px]">
          <!-- keep accessible label without affecting layout -->
          <label
            v-if="legoPlayersOnly && newEntry.game_uuid"
            for="event-name-select"
            class="sr-only"
          >
            Optional: Integration Event
          </label>

          <div v-if="legoPlayersOnly && newEntry.game_uuid">
            <!-- spinner sized like inputs so it aligns perfectly -->
            <div
              v-if="eventNames.loading"
              class="border rounded p-2 w-full h-[38px] flex items-center justify-start gap-2"
              role="status"
            >
              <LoadingComponent class="inline-block" />
              <span class="text-xs text-gray-500">Loading events…</span>
            </div>

            <select
              v-else
              id="event-name-select"
              v-model="newEntry.event_name"
              class="border rounded p-2 w-full h-[38px]"
              aria-label="Optional: Integration Event"
            >
              <option :value="''">(All events)</option>
              <option
                v-for="name in eventNames.items"
                :key="name"
                :value="name"
              >{{ name }}</option>
            </select>

            <p v-if="eventNames.error" class="text-xs text-red-600 mt-1">
              Failed to load event names.
            </p>
          </div>
        </div>

        <!-- Add -->
        <button
          @click="addEntry"
          :disabled="!canAdd"
          class="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          Add
        </button>
      </div>

      <!-- Entries list -->
      <ul class="mb-4 space-y-2">
        <li
          v-for="(e, i) in entries"
          :key="e.game_uuid + e.start_date + e.end_date + (e.event_name || '')"
          class="flex justify-between items-center bg-gray-50 rounded p-2"
        >
          <span>
            <strong>{{ e.game_name }}</strong>:
            {{ e.start_date }} → {{ e.end_date }}
            <span v-if="legoPlayersOnly && e.event_name" class="ml-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
              Event: {{ e.event_name }}
            </span>
          </span>
          <button
            @click="removeEntry(i)"
            class="text-red-500 hover:underline"
          >
            Remove
          </button>
        </li>
      </ul>

      <button
        @click="submitReport"
        :disabled="entries.length === 0 || loading"
        class="bg-green-600 text-white px-6 py-2 rounded disabled:opacity-50"
      >
        {{ loading ? 'Submitting…' : 'Submit Report' }}
      </button>
    </section>

    <!-- Reports List Section -->
    <section>
      <h2 class="text-2xl font-bold mb-4">Reports</h2>

      <!--upper pagination controls -->
      <nav class="flex justify-center space-x-4 mt-6" v-if="meta.last_page > 1">
        <button
          @click="page = page - 1; fetchReports()"
          :disabled="page === 1"
          class="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Prev
        </button>

        <span class="px-3 py-1">
          Page {{ page }} of {{ meta.last_page }}
        </span>

        <button
          @click="page = page + 1; fetchReports()"
          :disabled="page === meta.last_page"
          class="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </nav>

      <div class="space-y-6">
        <div
          v-for="report in reports"
          :key="report.id"
          class="bg-white shadow rounded p-6"
          :class="report.lego_players_only ? 'border-2 border-red-500' : 'border border-gray-200'"
        >
          <div class="flex justify-between items-center mb-2">
            <h3 class="text-xl font-semibold">
              Report #{{ report.id }}
              <span
                :class="{
                  'text-yellow-600': ['pending','running'].includes(report.status),
                  'text-green-600': report.status==='completed',
                  'text-red-600':   report.status==='failed'
                }"
                class="ml-2"
              >( {{ report.status }} )</span>

              <span
                v-if="report.lego_players_only"
                class="ml-3 inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-red-100 text-red-700 border border-red-300"
              >
                Integration Players Only
              </span>
            </h3>

            <div class="flex items-center space-x-2">
              <button
                @click="copyReport(report)"
                class="px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                title="Copy parameters to new report form"
              >
                Copy
              </button>

              <small class="text-gray-500 mx-2">
                <span v-if="report.requested_at">{{ new Date(report.requested_at).toLocaleString() }}</span>
                <span v-if="report.completed_at">— {{ new Date(report.completed_at).toLocaleString() }}</span>
              </small>

              <button
                @click="deleteReport(report.id)"
                class="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>

          <p class="mb-2">
            <strong>Timeframe:</strong>
            {{ report.overall_start_date }} – {{ report.overall_end_date }}
          </p>
          <p class="mb-4">
            <strong>Total Unique Players:</strong>
            {{ report.total_unique_count ?? '—' }}
          </p>

          <table class="w-full text-left border-collapse">
            <thead>
              <tr>
                <th class="border-b px-3 py-1">Game</th>
                <th class="border-b px-3 py-1">Start</th>
                <th class="border-b px-3 py-1">End</th>
                <th class="border-b px-3 py-1" v-if="report.lego_players_only">Event</th>
                <th class="border-b px-3 py-1">Unique</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="entry in report.entries"
                :key="entry.game_uuid + entry.start_date + entry.end_date + (entry.event_name || '')"
                class="hover:bg-gray-50"
              >
                <td class="px-3 py-1">{{ entry.game_name }}</td>
                <td class="px-3 py-1">{{ entry.start_date }}</td>
                <td class="px-3 py-1">{{ entry.end_date }}</td>
                <td class="px-3 py-1" v-if="report.lego_players_only">{{ entry.event_name || 'All events' }}</td>
                <td class="px-3 py-1">
                  {{ entry.unique_count != null ? Number(entry.unique_count).toLocaleString('en-US') : '…' }}
                </td>
              </tr>
            </tbody>
          </table>

          <div class="mt-4 flex items-center gap-3" v-if="report.status === 'completed'">
            <a
              v-if="report.csv_url"
              :href="report.csv_url"
              download
              class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            >
              Export CSV
            </a>
            <button
              v-else
              @click="downloadCsv(report)"
              class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <!-- pagination controls -->
      <nav class="flex justify-center space-x-4 mt-6" v-if="meta.last_page > 1">
        <button
          @click="page = page - 1; fetchReports()"
          :disabled="page === 1"
          class="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Prev
        </button>

        <span class="px-3 py-1">
          Page {{ page }} of {{ meta.last_page }}
        </span>

        <button
          @click="page = page + 1; fetchReports()"
          :disabled="page === meta.last_page"
          class="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </nav>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PlayerDataReporting',
  data() {
    return {
      games: [],
      gameFetchError: false,
      reports: [],
      page: 1,
      perPage: 15,
      meta: {},
      entries: [],
      newEntry: { game_uuid: '', game_name: '', start_date: '', end_date: '', event_name: '' },
      loading: false,
      maxSpanDays: 31,
      legoPlayersOnly: false,

      eventNames: {
        loading: false,
        error: false,
        items: [],
        lastUniverseId: null,
      },
    }
  },
  computed: {
    canAdd() {
      const { game_uuid, start_date, end_date } = this.newEntry
      if (!game_uuid || !start_date || !end_date) return false
      return !this.spanExceeded
    },
    spanExceeded() {
      const { start_date, end_date } = this.newEntry
      if (!start_date || !end_date) return false
      const start = new Date(start_date)
      const end = new Date(end_date)
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      return days > this.maxSpanDays
    },
    endMaxDate() {
      if (!this.newEntry.start_date) return null
      const d = new Date(this.newEntry.start_date)
      d.setDate(d.getDate() + this.maxSpanDays - 1)
      return d.toISOString().slice(0, 10)
    },
    selectedGame() {
      return this.games.find(g => g.uuid === this.newEntry.game_uuid) || null
    },
    selectedUniverseId() {
      return this.selectedGame ? Number(this.selectedGame.universe_id) : null
    },
  },
  watch: {
    legoPlayersOnly(val) {
      // Reset event selection if toggled off
      if (!val) {
        this.newEntry.event_name = ''
        this.eventNames = { loading: false, error: false, items: [], lastUniverseId: null }
      } else {
        // If toggled on and game + dates are selected, try to load events
        this.maybeFetchEventNames()
      }
    },
    'newEntry.game_uuid'() {
      this.newEntry.event_name = ''
      this.maybeFetchEventNames()
    },
    'newEntry.start_date'() {
      // dates don't affect the endpoint result but UX-wise we refresh only when game changes
      // left intentionally no-op besides validation
    },
    'newEntry.end_date'() {
      // same as above
    },
  },
  methods: {
    async fetchGames() {
      this.gameFetchError = false
      this.games = []

      const perPage = 500
      const url = p => `/player-data/games?page=${p}&per_page=${perPage}`

      const fetchPage = async (p = 1) => {
        const { data: payload } = await this.$http.get(url(p))
        if (Array.isArray(payload)) return { items: payload, lastPage: 1 }
        const items = Array.isArray(payload.data) ? payload.data : []
        const lastPage = Number(payload?.meta?.last_page) || 1
        return { items, lastPage }
      }

      try {
        const first = await fetchPage(1)
        const pages = Array.from({ length: first.lastPage - 1 }, (_, i) => i + 2)
        const rest = pages.length ? await Promise.all(pages.map(p => fetchPage(p))) : []
        const all = first.items.concat(...rest.map(r => r.items))

        // de-dup by uuid, then sort by friendly_name, then universe_id
        const map = new Map()
        all.forEach(g => { if (g?.uuid && !map.has(g.uuid)) map.set(g.uuid, g) })

        this.games = [...map.values()].sort((a, b) => {
          const n = (a.friendly_name || '').localeCompare(b.friendly_name || '')
          if (n) return n
          return (Number(a.universe_id) || 0) - (Number(b.universe_id) || 0)
        })
      } catch (e) {
        console.error(e)
        this.gameFetchError = true
      }
    },

    async fetchReports() {
      const qs = `?page=${this.page}&per_page=${this.perPage}`
      const res = await this.$http.get(`player-data-reports${qs}`)
      this.reports = res.data.data
      this.meta    = res.data.meta
    },

    onNewEntryGameChange() {
      // Keep friendly name handy for entries list
      const game = this.selectedGame
      this.newEntry.game_name = game?.friendly_name || ''
    },

    onSpanChange() {
      // Validation guard already handled; event list not span-dependent
      return
    },

    async maybeFetchEventNames() {
      // Only for integration-only + selected game
      if (!this.legoPlayersOnly || !this.selectedUniverseId) {
        this.eventNames = { loading: false, error: false, items: [], lastUniverseId: null }
        return
      }

      // Avoid refetch if same universe_id and we already have items
      if (this.eventNames.lastUniverseId === this.selectedUniverseId && this.eventNames.items.length) return

      this.eventNames.loading = true
      this.eventNames.error = false
      this.eventNames.items = []

      try {
        // Endpoint: GET /api/player-data/event-names?universe_id=...
        const { data } = await this.$http.get(`/player-data/event-names`, {
          params: { universe_id: this.selectedUniverseId }
        })

        const names = Array.isArray(data?.event_names) ? data.event_names : []
        // Deduplicate + sort defensively
        const uniq = [...new Set(names.filter(Boolean))].sort((a, b) => a.localeCompare(b))
        this.eventNames.items = uniq
        this.eventNames.lastUniverseId = this.selectedUniverseId

        // If previously selected name is not in the new list, reset to ''
        if (this.newEntry.event_name && !uniq.includes(this.newEntry.event_name)) {
          this.newEntry.event_name = ''
        }
      } catch (e) {
        console.error(e)
        this.eventNames.error = true
      } finally {
        this.eventNames.loading = false
      }
    },

    addEntry() {
      if (!this.canAdd) return
      const game = this.selectedGame
      this.entries.push({
        game_uuid: this.newEntry.game_uuid,
        game_name: game?.friendly_name || '',
        start_date: this.newEntry.start_date,
        end_date: this.newEntry.end_date,
        event_name: this.legoPlayersOnly ? (this.newEntry.event_name || '') : '', // optional
      })
      this.newEntry = { game_uuid: '', game_name: '', start_date: '', end_date: '', event_name: '' }
      // Clear event list cache for UX clarity on next selection (kept lightweight)
      this.eventNames = { loading: false, error: false, items: [], lastUniverseId: null }
    },

    removeEntry(index) {
      this.entries.splice(index, 1)
    },

    async submitReport() {
      if (!this.entries.length) return
      this.loading = true

      // snapshot for optimistic UI
      const snapshotEntries = this.entries.map(e => ({ ...e }))
      const starts = snapshotEntries.map(e => e.start_date).sort()
      const ends   = snapshotEntries.map(e => e.end_date).sort()
      const overall_start_date = starts[0]
      const overall_end_date   = ends[ends.length - 1]

      const payload = {
        lego_players_only: this.legoPlayersOnly ? 1 : 0,
        games: snapshotEntries.map(({ game_uuid, start_date, end_date, event_name }) => ({
          uuid: game_uuid,
          start_date,
          end_date,
          // Only send event_name if lego-only mode; otherwise omit to keep payload minimal
          ...(this.legoPlayersOnly && event_name ? { event_name } : {}),
        })),
      }

      try {
        const { data } = await this.$http.post('player-data-reports', payload)

        // optimistic item
        this.reports.unshift({
          id: data.report_id,
          status: 'pending',
          lego_players_only: this.legoPlayersOnly,
          overall_start_date,
          overall_end_date,
          entries: snapshotEntries.map(e => ({
            game_uuid: e.game_uuid,
            game_name: e.game_name,
            start_date: e.start_date,
            end_date: e.end_date,
            event_name: this.legoPlayersOnly ? (e.event_name || '') : undefined,
            unique_count: null,
          })),
        })

        this.entries = []
        this.pollStatus(data.report_id)
      } finally {
        this.loading = false
      }
    },

    async pollStatus(id) {
      const { data: { data } } = await this.$http.get('player-data-reports')
      const updated = data.find(r => r.id === id)
      if (!updated) return
      const idx = this.reports.findIndex(r => r.id === id)
      if (idx > -1) this.reports.splice(idx, 1, updated)
      if (['pending', 'running'].includes(updated.status)) {
        setTimeout(() => this.pollStatus(id), 2000)
      }
    },

    async deleteReport(id) {
      if (!confirm('Are you sure?')) return
      await this.$http.delete(`player-data-reports/${id}`)
      this.reports = this.reports.filter(r => r.id !== id)
    },

    downloadCsv(report) {
      const header = ['Game','Start','End', ...(report.lego_players_only ? ['Event'] : []), 'Unique']
      const rows = report.entries.map(e => {
        const base = [
          `"${(e.game_name || '').replace(/"/g,'""')}"`,
          e.start_date,
          e.end_date,
        ]
        if (report.lego_players_only) base.push(e.event_name || '')
        base.push(e.unique_count ?? '')
        return base.join(',')
      })
      const csv = [header.join(','), ...rows].join('\r\n')
      const gamePart = report.entries
        .map(e => (e.game_name || '').replace(/\s+/g,''))
        .join('_') || 'All_Games'
      const legoPrefix = report.lego_players_only ? 'INTEGRATION_PLAYERS_ONLY_' : ''
      const filename = `Report_${legoPrefix}${report.id}_${gamePart}_${(report.overall_start_date || '').replace(/,/g,'')}_to_${(report.overall_end_date || '').replace(/,/g,'')}.csv`
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    copyReport(report) {
      if (!report || !Array.isArray(report.entries)) return
      this.legoPlayersOnly = !!report.lego_players_only
      this.entries = report.entries.map(e => ({
        game_uuid: e.game_uuid,
        game_name: e.game_name || '',
        start_date: e.start_date,
        end_date: e.end_date,
        event_name: this.legoPlayersOnly ? (e.event_name || '') : '',
      }))
      this.newEntry = { game_uuid: '', game_name: '', start_date: '', end_date: '', event_name: '' }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
  },
  mounted() {
    this.fetchGames()
    this.fetchReports()
  }
}
</script>

<style scoped>
/* Tailwind covers styling */
</style>